import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2, Check, X, Loader2, GitMerge } from "lucide-react";
import type { Item } from "./useTagCategoryList";

type Props = {
  item: Item;
  isEditing: boolean;
  isSaving: boolean;
  editValue: string;
  setEditValue: (v: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onMerge: () => void;
  canMerge: boolean;
};

export const TagCategoryRow = ({
  item, isEditing, isSaving, editValue, setEditValue,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete, onMerge, canMerge,
}: Props) => (
  <li className="flex items-center gap-1 sm:gap-2 py-2 min-w-0">
    {isEditing ? (
      <>
        <Input
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSaveEdit();
            if (e.key === "Escape") onCancelEdit();
          }}
          className="h-8"
        />
        <Button size="icon" variant="ghost" onClick={onSaveEdit} disabled={isSaving} aria-label="Save">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </Button>
        <Button size="icon" variant="ghost" onClick={onCancelEdit} disabled={isSaving} aria-label="Cancel">
          <X className="h-4 w-4" />
        </Button>
      </>
    ) : (
      <>
        <span className="flex-1 truncate text-sm min-w-0">{item.name}</span>
        <Badge variant="secondary" className="shrink-0 hidden sm:inline-flex">
          {item.count} {item.count === 1 ? "excerpt" : "excerpts"}
        </Badge>
        <Badge
          variant="secondary"
          className="shrink-0 sm:hidden px-1.5"
          title={`${item.count} ${item.count === 1 ? "excerpt" : "excerpts"}`}
        >
          {item.count}
        </Badge>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 sm:h-10 sm:w-10"
          onClick={onStartEdit}
          aria-label="Rename"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 sm:h-10 sm:w-10"
          onClick={onMerge}
          aria-label="Merge"
          disabled={!canMerge}
        >
          <GitMerge className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 sm:h-10 sm:w-10 text-destructive hover:text-destructive"
          onClick={onDelete}
          aria-label="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </>
    )}
  </li>
);
